import React, { useMemo, useState } from "react";
import { TabHeader } from "components/TabHeader";
import { Button } from "newui/components/Button";
import { InputWithLabel } from "newui/components/ui/InputWithLabel";
import { DateRangeFilter, DateRangeValue } from "newui/components/Filters/DateRangeFilter";
import { TableColumnFilter } from "newui/components/Filters/TableColumnFilter";
import { useAuditLog } from "newui/features/RolesPermissions/hooks/useAuditLog";
import {
	AUDIT_CONTEXT_TYPES,
	AuditActionCategory,
	AuditLogFilters,
	AuditLogResource,
	formatAuditContextTypeLabel,
} from "newui/features/RolesPermissions/types/RolesPermissions.type";
import { AuditTrailTable } from "./AuditTrailTable";
import { AuditEventDetailModal } from "./AuditEventDetailModal";
import { AuditExportButton } from "./AuditExportButton";

export interface AuditTrailPageProps {
	path?: string;
}

const ACTION_CATEGORY_OPTIONS: Array<{ label: string; value: AuditActionCategory }> = [
	{ label: "Security", value: "security" },
	{ label: "RFx Change", value: "rfx_mutation" },
	{ label: "General", value: "general" },
];

const CONTEXT_TYPE_OPTIONS = AUDIT_CONTEXT_TYPES.map((contextType) => ({
	label: formatAuditContextTypeLabel(contextType.name),
	value: contextType.id,
}));

export const AuditTrailPage = (_props: AuditTrailPageProps) => {
	const [actionCategory, setActionCategory] = useState<AuditActionCategory[]>([]);
	const [contextType, setContextType] = useState<number[]>([]);
	const [actor, setActor] = useState("");
	const [dateRange, setDateRange] = useState<DateRangeValue>({ from: null, to: null });
	const [page, setPage] = useState(1);
	const [selectedEvent, setSelectedEvent] = useState<AuditLogResource | null>(null);

	const filters: AuditLogFilters = useMemo(
		() => ({
			action_category: actionCategory[0],
			context_type: contextType[0],
			actor: actor.trim() || undefined,
			from: dateRange.from ?? undefined,
			to: dateRange.to ?? undefined,
			page,
			per_page: 25,
		}),
		[actionCategory, contextType, actor, dateRange, page]
	);

	const { data, isLoading } = useAuditLog(filters);

	const events = data?.data ?? [];
	const meta = data?.meta;

	return (
		<div data-testid="audit-trail-page">
			<TabHeader
				heading="Audit Trail"
				description="Review security, RFx, and general activity across your company."
			/>

			<div className="mt-5 flex flex-wrap items-end justify-between gap-3">
				<div className="flex flex-wrap items-end gap-3">
					<TableColumnFilter
						label="Category"
						options={ACTION_CATEGORY_OPTIONS}
						value={actionCategory}
						onChange={(value) => {
							setActionCategory(value);
							setPage(1);
						}}
					/>
					<TableColumnFilter
						label="Context"
						options={CONTEXT_TYPE_OPTIONS}
						value={contextType}
						onChange={(value) => {
							setContextType(value);
							setPage(1);
						}}
					/>
					<DateRangeFilter
						label="Date"
						value={dateRange}
						onChange={(value) => {
							setDateRange(value);
							setPage(1);
						}}
					/>
					<div className="w-56">
						<InputWithLabel
							label="Actor"
							placeholder="Search by actor"
							value={actor}
							onChange={(e) => {
								setActor(e.target.value);
								setPage(1);
							}}
							testId="audit-actor-filter"
						/>
					</div>
				</div>

				<AuditExportButton
					filters={{
						action_category: filters.action_category,
						context_type: filters.context_type,
						actor: filters.actor,
						from: filters.from,
						to: filters.to,
					}}
				/>
			</div>

			<div className="mt-4">
				<AuditTrailTable events={events} isLoading={isLoading} onSelectEvent={setSelectedEvent} />
			</div>

			{meta && meta.last_page > 1 && (
				<div className="mt-4 flex items-center justify-between text-sm text-gray-600">
					<span>
						Page {meta.current_page} of {meta.last_page} ({meta.total} events)
					</span>
					<div className="flex gap-2">
						<Button
							btntype="secondary"
							size="sm"
							data-testid="audit-page-prev"
							disabled={meta.current_page <= 1}
							onClick={() => setPage((prev) => Math.max(1, prev - 1))}
						>
							Previous
						</Button>
						<Button
							btntype="secondary"
							size="sm"
							data-testid="audit-page-next"
							disabled={meta.current_page >= meta.last_page}
							onClick={() => setPage((prev) => Math.min(meta.last_page, prev + 1))}
						>
							Next
						</Button>
					</div>
				</div>
			)}

			<AuditEventDetailModal
				isOpen={selectedEvent !== null}
				event={selectedEvent}
				onClose={() => setSelectedEvent(null)}
			/>
		</div>
	);
};
